import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

interface ContainerProps {
  children: React.ReactNode
  columns?: 'default' | 'golden' | 'halves' | 'full' | null | undefined
  padding?: 'large-alt' | 'small-alt' | 'large' | 'small' | null | undefined
  config?: {
    sectionName: string
    sectionID?: string
    backgroundColour?: AllBackgroundColours
    edgetoedgeBackground?: boolean
    verticalAlignment?: string
    accentImage?: { asset: { sys: { uri: string } } }
    blockAlignment?: 'left' | 'centre' | null | undefined
  }
  className?: string
}

const section = cva('', {
  variants: {
    colour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l text-blue-d',
      chartreuse: 'bg-char text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-green text-blue-d',
      'light-orange': 'bg-orange text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d dark:bg-black dark:text-white',
      white: 'bg-white dark:bg-black'
    },
    columns: {
      default: 'lg:grid-cols-3',
      golden: 'md:grid-cols-2 lg:grid-cols-3',
      halves: 'md:grid-cols-2',
      full: 'md:grid-cols-1'
    },
    padding: {
      'large-alt': 'pb-xl sm:pb-2xl lg:pb-3xl',
      'small-alt': 'pb-l sm:pb-xl lg:pb-2xl pt-[40px]',
      large: 'py-xl sm:py-2xl lg:py-3xl',
      small: 'py-l sm:py-xl lg:py-2xl',
      none: 'px-0'
    },
    alignment: {
      left: 'place-content-start',
      centre: 'place-content-center'
    }
  },
  defaultVariants: {
    columns: 'default',
    padding: 'small',
    colour: 'white',
    alignment: 'centre'
  }
})

const Container = (props: ContainerProps) => {
  const { children, columns, padding, config, className } = props

  return (
    <>
      <section
        aria-label={config?.sectionName}
        className={cn(
          'lg:scroll-mt-2xl scroll-mt-xl',
          config?.edgetoedgeBackground ? '' : 'lg:mx-xs-plus',
          config?.accentImage ? 'relative' : '',
          // config?.edgetoedgeBackground &&
          //   'gap-l grid min-h-[80px] w-full grid-flow-row grid-cols-1',
          config?.edgetoedgeBackground
            ? section({
                columns: null,
                padding: null,
                colour: config?.backgroundColour,
                alignment: null
              })
            : ''
        )}
        {...(config?.sectionID !== '' ? { id: config?.sectionID } : {})}
      >
        {config?.accentImage && (
          <div
            className='lg:w-s bg-blue-d w-2xs-plus absolute left-0 h-full mix-blend-hard-light'
            style={{
              backgroundImage: `url(${config?.accentImage?.asset.sys.uri}?crop=24,1080,right&q=75&f=webp)`,
              backgroundSize: 'cover',
              opacity: 1
            }}
          ></div>
        )}
        <div
          className={cn(
            'gap-m sm:gap-l lg:gap-2xl min-h-2xl relative container grid w-full grid-flow-row grid-cols-1',
            !config?.edgetoedgeBackground ? 'px-[28px]' : '',
            config?.blockAlignment === 'centre'
              ? '[&>div]:justify-self-center [&>div>div]:mx-auto'
              : '',
            config?.verticalAlignment === 'top'
              ? 'items-start'
              : 'items-center',
            !config?.edgetoedgeBackground
              ? section({
                  columns: columns,
                  padding: padding,
                  colour: config?.backgroundColour,
                  alignment: null
                })
              : section({
                  columns: columns,
                  padding: padding,
                  colour: null,
                  alignment: null
                }),
            className
          )}
        >
          {children}
        </div>
      </section>
    </>
  )
}

export default Container
